import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../molecules/Dialog";
import { Loader2 } from "lucide-react";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import {
  useDisclaimerQuery,
  useExercisesQuery,
} from "@/app/exercises/exercises.api";
import { t } from "i18next";

type TData = {
  id: number;
  name: string;
  url: string;
  description: string;
  review?: string;
  second_main_muscle?: string;
  second_secondary_muscle?: string;
  img: string;
  videoUrl: string;
  imageUrl: string;
  image_url: string;
  video_url: string;
  index: number;
  first_alternative?: string;
};

type Props = {
  data: TData;
  open: boolean;
  onClose: () => void;
  onSelectChange: ({
    newExercise,
    index,
  }: {
    newExercise: TData;
    index: number;
  }) => void;
};

export function ExercisePreview({
  data,
  open,
  onClose,
  onSelectChange,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredExercise, setHoveredExercise] = useState<number | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<TData | null>(null);
  const [currentData, setCurrentData] = useState<TData>(data);

  const { data: exercisesQuery, isLoading: isLoadingExercisesQuery }: any =
    useDisclaimerQuery({
      firstAlternative: data?.first_alternative,
    });

  const handleExerciseSelect = useCallback((exercise: TData) => {
    setSelectedExercise(exercise);
    setCurrentData(exercise);
  }, []);

  const handleRevert = useCallback(() => {
    setSelectedExercise(null);
    setCurrentData(data);
  }, [data]);

  const handleChange = useCallback(() => {
    if (selectedExercise) {
      onSelectChange({
        newExercise: {
          ...selectedExercise,
        },
        index: data.index,
      });
      setSelectedExercise(null);
      setCurrentData(data);
      onClose();
    }
  }, [selectedExercise, data, onSelectChange, onClose]);

  useEffect(() => {
    setCurrentData(data);
  }, [data]);

  useEffect(() => {
    if (exercisesQuery?.data && exercisesQuery?.data?.length > 0) {
      setIsExpanded(true);
    }
  }, [exercisesQuery]);

  const ReviewTips = useMemo(() => {
    if (
      !currentData?.review ||
      currentData.review === "NULL" ||
      !currentData.review.trim()
    ) {
      return null;
    }
    const tips = currentData.review.split("/b").filter(Boolean);
    return (
      <div className="p-4 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
        {tips?.map((tip, index) => (
          <span
            key={index}
            style={{ display: "block", marginTop: "16px" }}
            className="text-gray-600"
          >
            {"  "}• {tip?.trim?.()}{" "}
          </span>
        ))}
      </div>
    );
  }, [currentData?.review]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={`w-11/12 ${isExpanded ? "max-w-5xl" : "max-w-md"} h-auto max-h-[90vh] p-0 overflow-hidden transition-all duration-500 ease-in-out bg-white`}
      >
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>{t("trainingEditor.preview.exerciseDetails")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col md:flex-row h-full overflow-hidden">
          <div
            className={`p-6 rounded-lg m-1 ${isExpanded ? "md:w-1/2" : "w-full"} overflow-y-auto`}
          >
            {(currentData?.url ||
              currentData?.videoUrl ||
              currentData?.video_url) && (
              <div>
                <video
                  loop
                  src={
                    currentData.url ||
                    currentData.videoUrl ||
                    currentData?.video_url
                  }
                  muted
                  autoPlay
                  className="w-full h-auto"
                />
              </div>
            )}
            {ReviewTips}
            <div className="flex flex-col gap-4 p-4">
              {currentData?.second_main_muscle && (
                <div className="flex flex-col gap-2 bg-gray-50 p-4 rounded-lg">
                  <label>{t("trainingEditor.preview.mainMuscle")}</label>
                  <span className="text-gray-600">
                    {currentData.second_main_muscle}
                  </span>
                </div>
              )}
              {currentData?.second_secondary_muscle && (
                <div className="flex flex-col gap-2 bg-gray-50 p-4 rounded-lg">
                  <label>{t("trainingEditor.preview.secondaryMuscle")}</label>
                  <span className="text-gray-600">
                    {currentData?.second_secondary_muscle}
                  </span>
                </div>
              )}
            </div>
            {selectedExercise && (
              <div className="mt-4 flex gap-4">
                <Button onClick={handleRevert}>{t("trainingEditor.preview.revert")}</Button>
                <Button
                  onClick={handleChange}
                  className="bg-primary-500 hover:bg-primary-600 text-white"
                >
                  {t("trainingEditor.preview.changeToEquivalent")}
                </Button>
              </div>
            )}
          </div>

          <AnimatePresence>
            {isExpanded && data?.first_alternative && (
              <motion.div
                key="expanded-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="p-6 rounded-lg m-4 md:w-2/3 overflow-y-auto flex flex-col h-full"
              >
                <h3 className="text-lg font-semibold mb-2">
                  {t("trainingEditor.preview.equivalentExercises")}
                </h3>
                {isLoadingExercisesQuery ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center h-64"
                  >
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <p className="mt-4">{t("trainingEditor.preview.loadingExercises")}</p>
                  </motion.div>
                ) : exercisesQuery?.data?.length > 0 ? (
                  <motion.div
                    key="info"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="overflow-y-auto flex-grow"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-2">
                      {exercisesQuery?.data?.map((exercise: TData) => (
                        <motion.div
                          key={exercise.id}
                          className={`p-1 rounded bg-gray-200 w-full h-40 relative overflow-hidden cursor-pointer ${
                            selectedExercise?.id === exercise.id
                              ? "border-2 border-blue-500"
                              : ""
                          }`}
                          onMouseEnter={() => setHoveredExercise(exercise?.id)}
                          onMouseLeave={() => setHoveredExercise(null)}
                          onClick={() => handleExerciseSelect(exercise)}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <AnimatePresence>
                            {hoveredExercise === exercise.id ? (
                              <motion.video
                                key="video"
                                loop
                                src={
                                  exercise.videoUrl ||
                                  exercise.url ||
                                  exercise?.video_url
                                }
                                muted
                                autoPlay
                                className="w-full h-full absolute top-0 left-0"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                              />
                            ) : (
                              <motion.img
                                key="image"
                                src={exercise.imageUrl || exercise?.image_url}
                                className="w-full h-full"
                                alt={exercise.name}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                              />
                            )}
                          </AnimatePresence>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <p className="text-gray-500">{t("trainingEditor.preview.noExercisesFound")}</p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}